import "url-polyfill";
import "@/polyfills/nodelist-foreach";
import events from "@/events";
import notify from "@/utils/notify";
import { getCurrentPageUrl } from "@/utils/location";

const ADJUST_BASE_URL = "https://app.adjust.com";
const IOS_APP_CODE = "ql5itzy";
const ANDROID_APP_CODE = "w1rynlu";

const createLabelParam = (fingerprint, buttonCode, testCategories) => {
  const currentPageUrl = getCurrentPageUrl();
  const searchParams = currentPageUrl.searchParams;
  const pathname = currentPageUrl.pathname.replace("/", "").replace(".html", "");

  const utmCampaign = searchParams.get("utm_campaign") || "";
  const utmTerm = searchParams.get("utm_term") || "";
  const utmContent = searchParams.get("utm_content") || "";
  const code = buttonCode || "body-20190405-adjust_link";
  const categories = Object.keys(testCategories)
    .map(key => `${key}-${testCategories[key]}`)
    .join(",");

  return `${utmCampaign}__${utmTerm}__${utmContent}__${code}__${categories}__${pathname}__${fingerprint}`;
};

const createCampaignParam = () => {
  const href = getCurrentPageUrl();
  if (href.searchParams.has("utm_source")) {
    return href.searchParams.get("utm_source");
  } else {
    return "organic";
  }
};

export const init = () => {
  events.on("setup:ready", config => {
    document.querySelectorAll(".js-adjust-link-track").forEach(node => {
      const buttonCode = node.dataset.buttonCode;
      const platform = node.dataset.platform;

      let targetUrl;
      const baseConfig = {
        buttonCode,
        testCategories: config.test_categories,
        fingerprint: config.fingerprint
      };
      if (platform === "has_mobile_app") {
        const deeplinkPath = node.dataset.deeplinkPath;
        targetUrl = deeplinkUrl({ ...baseConfig, deeplinkPath });
      } else {
        targetUrl = adjustUrl({ ...baseConfig, platform });
      }

      node.setAttribute("href", targetUrl);
    });
  });
};

// Example link: https://k8vu.adj.st/?adj_t=ql5itzy_w1rynlu&adj_campaign=organic&adj_label=______body-20200123-store_badges____audiobooks%2Fqwer__c588cdde-1371-4a9b-808d-ec7da6922482&adj_deeplink=blinkistapp%3A%2F%2F
export const deeplinkUrl = ({
  deeplinkPath = "",
  fingerprint,
  buttonCode,
  testCategories
}) => {
  if (!fingerprint) {
    notify("Fingerprint is null. Did you wait for 'setup:ready' before calling the method?");
    return;
  }

  // Use an adjust based universal link to deeplink into the app
  const url = new URL(`https://k8vu.adj.st/${deeplinkPath}`);
  // Add the app codes as fallback in case the app is not installed
  url.searchParams.set("adj_t", `${IOS_APP_CODE}_${ANDROID_APP_CODE}`);
  url.searchParams.set("adj_deeplink", `blinkistapp://${deeplinkPath}`);
  url.searchParams.set("adj_campaign", createCampaignParam());
  url.searchParams.set("adj_label", createLabelParam(fingerprint, buttonCode, testCategories));

  return url.href;
};

export const adjustUrl = ({
  platform,
  fingerprint,
  buttonCode,
  testCategories,
  androidWebToMobileFlag
}) => {
  if (!fingerprint) {
    notify("Fingerprint is null. Did you wait for 'setup:ready' before calling the method?");
    return;
  }

  let url;
  if (platform === "ios") {
    url = new URL(`${ADJUST_BASE_URL}/${IOS_APP_CODE}`);
  } else if (platform === "android") {
    url = new URL(`${ADJUST_BASE_URL}/${ANDROID_APP_CODE}`);
  } else {
    url = new URL(`${ADJUST_BASE_URL}/${IOS_APP_CODE}_${ANDROID_APP_CODE}`);
  }

  url.searchParams.set("campaign", createCampaignParam());
  url.searchParams.set("label", createLabelParam(fingerprint, buttonCode, testCategories));

  // For android we can pass through a minimal set of metadata to the app even if the user has to download it first
  // via the appstore.
  // We kinda abuse the `creative` param of the adjust URL here to pass this through and give the android app a sign
  // where on web this user is coming from.
  // => See the conversation on this ticket: https://blinkist.atlassian.net/browse/PAN-735
  if (androidWebToMobileFlag) {
    url.searchParams.set("creative", androidWebToMobileFlag);
  }

  return url.href;
};
